import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, gbp } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ALL = "__ALL__";

function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
}

export default function AdminProfit() {
  const [stores, setStores] = useState([]);
  const [filters, setFilters] = useState({ start_date: daysAgoISO(28), end_date: daysAgoISO(0), store_id: ALL });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadStores = useCallback(async () => {
    try { const { data } = await api.get("/stores"); setStores(data); } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.store_id && filters.store_id !== ALL) params.store_id = filters.store_id;
      const { data } = await api.get("/reports/profit", { params });
      setReport(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadStores(); }, [loadStores]);
  useEffect(() => { load(); }, [load]);

  const reset = () => setFilters({ start_date: daysAgoISO(28), end_date: daysAgoISO(0), store_id: ALL });

  const profitColor = (p) => (p > 0 ? "text-emerald-700" : p < 0 ? "text-[#DF1A22]" : "text-zinc-700");
  const r = report || { net_sales: 0, labour_cost: 0, food_cost: 0, total_cost: 0, profit: 0, profit_percent: 0 };

  // Build a simple horizontal bar visualisation of cost composition vs net sales
  const denom = Math.max(r.net_sales, r.total_cost, 1);
  const labourPct = (r.labour_cost / denom) * 100;
  const foodPct = (r.food_cost / denom) * 100;
  const profitPct = (Math.max(r.profit, 0) / denom) * 100;

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="label-uc">Reporting</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Profit & Loss</h1>
          <p className="text-sm text-zinc-500 mt-1">Net sales minus labour and food cost over the selected period.</p>
        </div>
        <Button variant="outline" onClick={reset} className="rounded-none border-zinc-300" data-testid="profit-filters-reset">Reset</Button>
      </div>

      <div className="bg-white border border-zinc-200 p-4 grid md:grid-cols-3 gap-3 mb-6" data-testid="profit-filters-bar">
        <div>
          <Label className="label-uc">From</Label>
          <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            className="rounded-none border-zinc-300 mt-1.5 font-mono-data" data-testid="profit-filter-start" />
        </div>
        <div>
          <Label className="label-uc">To</Label>
          <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            className="rounded-none border-zinc-300 mt-1.5 font-mono-data" data-testid="profit-filter-end" />
        </div>
        <div>
          <Label className="label-uc">Store</Label>
          <Select value={filters.store_id} onValueChange={(v) => setFilters({ ...filters, store_id: v })}>
            <SelectTrigger className="rounded-none border-zinc-300 mt-1.5" data-testid="profit-filter-store">
              <SelectValue placeholder="All stores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All stores</SelectItem>
              {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && <div className="text-sm text-zinc-500">Loading…</div>}

      {/* Top KPIs */}
      <div className="grid md:grid-cols-4 gap-0 border border-zinc-200 bg-white mb-6" data-testid="profit-kpis">
        <div className="p-5">
          <div className="label-uc">Net sales</div>
          <div className="mt-1 text-3xl font-bold font-mono-data" data-testid="kpi-net-sales">{gbp(r.net_sales)}</div>
        </div>
        <div className="p-5 border-l border-zinc-200">
          <div className="label-uc">Total cost</div>
          <div className="mt-1 text-3xl font-bold font-mono-data" data-testid="kpi-total-cost">{gbp(r.total_cost)}</div>
        </div>
        <div className="p-5 border-l border-zinc-200">
          <div className="label-uc">Profit</div>
          <div className={`mt-1 text-3xl font-bold font-mono-data ${profitColor(r.profit)}`} data-testid="kpi-profit">{gbp(r.profit)}</div>
        </div>
        <div className="p-5 border-l border-zinc-200 bg-zinc-950 text-white">
          <div className="label-uc text-zinc-400">Profit %</div>
          <div className="mt-1 text-3xl font-bold font-mono-data" data-testid="kpi-profit-percent">{r.profit_percent}%</div>
        </div>
      </div>

      {/* Cost breakdown */}
      <div className="bg-white border border-zinc-200 p-5 mb-6">
        <div className="label-uc mb-3">Cost composition</div>
        <div className="w-full h-3 bg-zinc-100 flex" aria-hidden>
          <div className="bg-zinc-950" style={{ width: `${labourPct}%` }} data-testid="bar-labour" />
          <div className="bg-[#DF1A22]" style={{ width: `${foodPct}%` }} data-testid="bar-food" />
          <div className="bg-emerald-600" style={{ width: `${profitPct}%` }} data-testid="bar-profit" />
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
          <div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 bg-zinc-950" /><span className="label-uc">Labour</span></div>
            <div className="font-mono-data text-lg mt-1" data-testid="row-labour-cost">{gbp(r.labour_cost)}</div>
          </div>
          <div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#DF1A22]" /><span className="label-uc">Food</span></div>
            <div className="font-mono-data text-lg mt-1" data-testid="row-food-cost">{gbp(r.food_cost)}</div>
          </div>
          <div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 bg-emerald-600" /><span className="label-uc">Profit</span></div>
            <div className={`font-mono-data text-lg mt-1 ${profitColor(r.profit)}`}>{gbp(r.profit)}</div>
          </div>
        </div>
      </div>

      {/* Detail table */}
      <div className="bg-white border border-zinc-200">
        <table className="w-full text-sm" data-testid="profit-detail-table">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="px-4 py-2 label-uc">Line</th>
              <th className="px-4 py-2 label-uc text-right">Amount</th>
              <th className="px-4 py-2 label-uc text-right">% of Net sales</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-zinc-100">
              <td className="px-4 py-2">Net sales (revenue)</td>
              <td className="px-4 py-2 text-right font-mono-data">{gbp(r.net_sales)}</td>
              <td className="px-4 py-2 text-right font-mono-data">100.00%</td>
            </tr>
            <tr className="border-t border-zinc-100">
              <td className="px-4 py-2">Labour cost</td>
              <td className="px-4 py-2 text-right font-mono-data">{gbp(r.labour_cost)}</td>
              <td className="px-4 py-2 text-right font-mono-data">{r.net_sales > 0 ? ((r.labour_cost / r.net_sales) * 100).toFixed(2) : "0.00"}%</td>
            </tr>
            <tr className="border-t border-zinc-100">
              <td className="px-4 py-2">Food cost</td>
              <td className="px-4 py-2 text-right font-mono-data">{gbp(r.food_cost)}</td>
              <td className="px-4 py-2 text-right font-mono-data">{r.net_sales > 0 ? ((r.food_cost / r.net_sales) * 100).toFixed(2) : "0.00"}%</td>
            </tr>
            <tr className="border-t-2 border-zinc-300 bg-zinc-50">
              <td className="px-4 py-2 font-semibold">Total cost</td>
              <td className="px-4 py-2 text-right font-mono-data font-semibold">{gbp(r.total_cost)}</td>
              <td className="px-4 py-2 text-right font-mono-data font-semibold">{r.net_sales > 0 ? ((r.total_cost / r.net_sales) * 100).toFixed(2) : "0.00"}%</td>
            </tr>
            <tr className="border-t-2 border-zinc-950 bg-zinc-100">
              <td className="px-4 py-2 font-bold">Profit</td>
              <td className={`px-4 py-2 text-right font-mono-data font-bold ${profitColor(r.profit)}`}>{gbp(r.profit)}</td>
              <td className={`px-4 py-2 text-right font-mono-data font-bold ${profitColor(r.profit)}`}>{r.profit_percent}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
